import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { ROUTER_BASENAME } from '../lib/base';
import { getLocale } from '../lib/i18n';
import {
  setCanonical,
  setLink,
  setMeta,
  setTitle,
  usePublicSeo,
  type PublicSeoConfig,
} from '../lib/seo';

/**
 * <Seo> — manejador de <head> por página (client-side).
 *
 * Cada página pública monta un <Seo> con su título/descripción; el componente
 * lee la config del Super Admin (usePublicSeo) + el locale runtime y hace upsert
 * de title/description/canonical/OpenGraph/Twitter/hreflang en document.head.
 *
 * noindex: se fuerza si `noindex` prop, si el admin apagó la indexación
 * (robots_index=false), o si el HOST actual NO coincide con el de canonical_base
 * (protección para que preview/staging nunca se indexe con el dominio de prod).
 */
export interface SeoProps {
  /** Título de la página (sin el nombre del sitio; se compone con " · "). */
  title?: string;
  /** Descripción meta. Si falta, cae al default del idioma. */
  description?: string;
  /** Path canónico. Default = location.pathname (dentro del basename). */
  path?: string;
  /** Imagen OG/Twitter absoluta. Default = og_image de la config. */
  image?: string;
  /** og:type. Default 'website'. */
  type?: string;
  /** Fuerza <meta robots noindex,nofollow>. */
  noindex?: boolean;
}

export function Seo({ title, description, path, image, type = 'website', noindex }: SeoProps) {
  const { data: config } = usePublicSeo();
  const location = useLocation();
  const locale = getLocale();

  useEffect(() => {
    applySeo({ config, locale, title, description, path: path ?? location.pathname, image, type, noindex });
    // getLocale() se lee en render; el árbol re-renderiza al cambiar de idioma.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, locale, title, description, path, location.pathname, image, type, noindex]);

  return null;
}

interface ApplyArgs {
  config?: PublicSeoConfig;
  locale: 'es' | 'pt';
  title?: string;
  description?: string;
  path: string;
  image?: string;
  type: string;
  noindex?: boolean;
}

function applySeo(args: ApplyArgs): void {
  const { config, locale, title, description, path, image, type, noindex } = args;

  const siteName = config?.site_name ?? 'Tembiapo';
  const langDefaults = config?.defaults?.[locale];

  // --- title ---
  // Con prop: "{prop} · {site}". Sin prop (home): sólo el default del idioma.
  const effectiveTitle = title
    ? `${title} · ${siteName}`
    : langDefaults?.title ?? siteName;
  setTitle(effectiveTitle);

  // --- description / keywords ---
  const effectiveDesc = description ?? langDefaults?.description ?? '';
  setMeta('name', 'description', effectiveDesc);
  setMeta('name', 'keywords', langDefaults?.keywords ?? '');

  // --- canonical (absoluta) ---
  const canonical = buildCanonical(config?.canonical_base ?? '', path);
  if (canonical) setCanonical(canonical);

  // --- robots ---
  const wrongHost = canonical ? !hostMatches(config?.canonical_base ?? '') : false;
  const blocked = !!noindex || config?.robots_index === false || wrongHost;
  setMeta('name', 'robots', blocked ? 'noindex,nofollow' : 'index,follow');

  // --- Open Graph ---
  const ogImage = image || config?.og_image || '';
  setMeta('property', 'og:title', effectiveTitle);
  setMeta('property', 'og:description', effectiveDesc);
  setMeta('property', 'og:type', type);
  setMeta('property', 'og:url', canonical);
  setMeta('property', 'og:image', ogImage);
  setMeta('property', 'og:site_name', siteName);
  setMeta('property', 'og:locale', locale === 'pt' ? 'pt_BR' : 'es_ES');

  // --- Twitter ---
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', effectiveTitle);
  setMeta('name', 'twitter:description', effectiveDesc);
  setMeta('name', 'twitter:image', ogImage);
  setMeta('name', 'twitter:site', config?.twitter_handle ?? '');

  // --- hreflang ---
  // El idioma es runtime (no hay URL por idioma) => ambos alternate y x-default
  // apuntan a la misma canónica.
  if (canonical) {
    setLink('alternate', canonical, 'es');
    setLink('alternate', canonical, 'pt-BR');
    setLink('alternate', canonical, 'x-default');
  }
}

/**
 * Construye la URL canónica absoluta. `base` es el origen público (puede o no
 * incluir el path base de la app). Añade el basename del router sólo si `base`
 * no lo incluye ya (evita /tembiapo/tembiapo en preview bajo path).
 */
function buildCanonical(base: string, path: string): string {
  if (!base) return '';
  let root = base.replace(/\/+$/, '');
  const bp = ROUTER_BASENAME; // "" en raíz, "/tembiapo" bajo path
  if (bp && !root.endsWith(bp)) {
    root += bp;
  }
  const p = path.startsWith('/') ? path : `/${path}`;
  return root + p;
}

/** True si el host de la página actual coincide con el de canonical_base. */
function hostMatches(base: string): boolean {
  if (!base || typeof window === 'undefined') return true;
  try {
    return new URL(base).host === window.location.host;
  } catch {
    return true; // base malformada: no bloqueamos por las dudas
  }
}
